import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import { HeaderButton, Buttons, CircularIndeterminate } from "components";
import Filters from "components/Filters";
import Modal from "components/UI/Modal/Modal";
import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import Cached from "@mui/icons-material/Cached";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import ZoomOutMapRounded from "@mui/icons-material/ZoomOutMapRounded";
import ZoomInMapRounded from "@mui/icons-material/ZoomInMapRounded";
import PrintOutlined from "@mui/icons-material/PrintOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import styles from "styles/pages/Pages.module.scss";
import org from "./Organigrama.module.scss";
import NodoCascada from "./Components/NodoCascada";
import ConectoresCascada from "./Components/ConectoresCascada";
import ModalAsignacion from "./Actions/ModalAsignacion";
import ModalDetalleNodo from "./Actions/ModalDetalleNodo";
import { useArbolHospital, TIPO_DIR } from "./hooks/useArbolHospital";
import { TIPO, NOMBRE_TIPO, esContenedor } from "./utils/estructura";
import { imprimirArbol } from "./utils/imprimirArbol";

import { RNHZonaGet } from "services/services-mia/programa-salud/estructuras/RNHZonaGet";
import { RNHEstablecimientoGet } from "services/services-mia/programa-salud/estructuras/RNHEstablecimientoGet";
import { nombresDeNivelPorCod } from "../niveles/catalogo";

const COLUMNAS = ["Dirección", "Departamentos", "Divisiones", "Unidades", "CAPS", "Puestos/Postas/URESAs"];

/**
 * Organigrama de estructura hospitalaria — VISTA EN CASCADA.
 *
 * Trae toda la estructura del hospital (`listAll`) y la muestra como árbol escalonado:
 * cada nodo ocupa su fila en la columna de su tipo; al expandir una entidad, sus hijos
 * bajan a la derecha (multi-expansión: varias ramas a la vez). Gestos: click en card =
 * SELECCIONAR (target del botón Asignar); click en la flecha = EXPANDIR.
 */
export default function Organigrama() {
  const credentials = useRecoilValue(userCredentials);
  const setRoute = useSetRecoilState(routeInfo);
  const { service } = useService();

  /**
   * Asignar y desasignar estructura es escribir. Un Visualizador entra al organigrama, navega
   * el arbol y mira los agentes de cada nodo, pero no abre la gestion.
   *
   * `servicioPermiso === 'E'` alcanza aca: a diferencia de la vista de usuarios internos —donde
   * un Operador tiene 'E' por los usuarios EXTERNOS y hubo que mirar ademas los roles que
   * puede asignar— la estructura no esta partida en dos universos. Quien puede escribir en el
   * servicio puede gestionarla.
   */
  const puedeGestionar = puedeEditarServicio(service);

  // ── Filtros ────────────────────────────────────────────────────────────
  const [zonas, setZonas] = useState([]);
  const [hospitales, setHospitales] = useState([]);
  const [loadingZonas, setLoadingZonas] = useState(false);
  const [loadingHospitales, setLoadingHospitales] = useState(false);

  // Hospital de contexto → dispara la carga del árbol en el hook.
  const [hospitalSeleccionado, setHospitalSeleccionado] = useState(null);
  // Datos del hospital APLICADO para la card de info (capturados al filtrar, para que
  // sobrevivan a un cambio de zona que recargue la lista de hospitales).
  const [hospitalInfo, setHospitalInfo] = useState(null);

  const formik = useFormik({
    initialValues: { zonaCod: "", hospitalId: "" },
    onSubmit: (values) => {
      setHospitalSeleccionado(values.hospitalId || null);
      const hosp = hospitales.find((h) => String(h.rnhEstablecimientoId) === String(values.hospitalId)) || null;
      setHospitalInfo(hosp);
    },
  });
  const { zonaCod, hospitalId } = formik.values;

  // Árbol completo del hospital (carga todo + navegación client-side en cascada).
  const arbol = useArbolHospital(hospitalSeleccionado, credentials);

  // Modal de asignación + flag para refrescar la vista al cerrar si hubo altas/bajas.
  const [accionOpen, setAccionOpen] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);

  const [huboCambios, setHuboCambios] = useState(false);

  const marcarCambio = () => setHuboCambios(true);
  const refrescarSiCambio = () => {
    if (huboCambios) {
      arbol.refrescar(); // recarga el árbol manteniendo expansión y selección
      // Y además deja a la vista lo que se acaba de asignar. Lo nuevo cuelga del nodo que se
      // gestionó, así que se abre ese nodo y todo el camino hasta la raíz: sin esto, asignar
      // sobre una card colapsada no mostraba ningún cambio y parecía que no había pasado nada.
      arbol.expandirCamino(nodoGestionable?.rnhEstablecimientoEstructuraId);
      setHuboCambios(false);
    }
  };
  // Cierre del modal (lo usan tanto la X de arriba como el botón "Cerrar" de abajo).
  const cerrarAccion = () => {
    setAccionOpen(false);
    refrescarSiCambio();
  };

  const nodoAccion = arbol.nodoSeleccionado;

  // Target de "Gestionar": solo contenedores y la Dirección. Desde que las hojas
  // (Puesto, Posta, URESA) también se pueden seleccionar, hace falta filtrarlas acá:
  // el `else` final de la cadena de abajo cae en Departamento y les armaría un target
  // equivocado en vez de dejar el botón apagado.
  const nodoGestionable =
    nodoAccion &&
    (esContenedor(nodoAccion) || nodoAccion.rnhTipoEstructuraCod === TIPO_DIR)
      ? nodoAccion
      : null;
  // Contexto del hospital que viaja en TODOS los targets.
  /**
   * COMPONENTE MAXIMO del hospital: el techo de lo que se le puede colgar. Con "D" se
   * puede desde departamento para abajo, con "I" desde division y con "U" desde unidad.
   *
   * OJO CON EL PREFIJO: en la fila del establecimiento TODO viene como
   * `rnhEstablecimiento*`, incluidos los datos del nivel, la zona y el area, que estan
   * desnormalizados ahi adentro. No hay `rnhNivelComponenteMaximoCod` ni `rnhZonaNombre`
   * sueltos; leerlos asi da `undefined` sin ningun error.
   *
   * Eso es justo lo que pasaba: quedaba `undefined` y, como
   * `tiposHabilitadosPorComponenteMaximo` falla ABIERTO ante un valor desconocido, se
   * habilitaban TODAS las pestañas. Se podia entrar a una que el nivel no permite y el
   * rechazo llegaba recien del back, al asignar.
   */
  const ctxHosp = {
    hospitalId: hospitalSeleccionado,
    componenteMaximo: hospitalInfo?.rnhEstablecimientoNivelComponenteMaximoCod,
  };
  const targetAccion = !nodoGestionable
    ? null
    : nodoGestionable.rnhTipoEstructuraCod === TIPO_DIR
      // La Dirección es UI del front (sin id real): asignar "a la Dirección" = asignar
      // departamentos al HOSPITAL (target "H"). No se manda ningún id de dirección.
      ? { tipo: TIPO.DIRECCION, ...ctxHosp }
      : nodoGestionable.rnhTipoEstructuraCod === TIPO.DIVISION
        ? { tipo: TIPO.DIVISION, ...ctxHosp, departamentoId: nodoGestionable.rnhDepartamentoId, divisionId: nodoGestionable.rnhDivisionId }
        : nodoGestionable.rnhTipoEstructuraCod === TIPO.UNIDAD
          ? { tipo: TIPO.UNIDAD, ...ctxHosp, departamentoId: nodoGestionable.rnhDepartamentoId, divisionId: nodoGestionable.rnhDivisionId, unidadId: nodoGestionable.rnhUnidadId }
          : nodoGestionable.rnhTipoEstructuraCod === TIPO.CAPS
            ? { tipo: TIPO.CAPS, ...ctxHosp, departamentoId: nodoGestionable.rnhDepartamentoId, divisionId: nodoGestionable.rnhDivisionId, unidadId: nodoGestionable.rnhUnidadId, capsId: nodoGestionable.rnhCapsId }
            : { tipo: TIPO.DEPARTAMENTO, ...ctxHosp, departamentoId: nodoGestionable.rnhDepartamentoId };

  // Carga inicial de zonas.
  useEffect(() => {
    (async () => {
      setLoadingZonas(true);
      const { data: res } = await RNHZonaGet(credentials);
      setZonas(Array.isArray(res) ? res : []);
      setLoadingZonas(false);
    })();
  }, [credentials]);

  // El título va en un layout effect para que el Header no alcance a pintar el anterior.
  useLayoutEffect(() => {
    setRoute({ wordKey: "Organigrama - Programa Salud RN" });
  }, [setRoute, service.servicioNombre]);

  // Zona: "Todos" (vacío) → todos los hospitales; una zona → los de esa zona.
  // Corre también en el mount (zonaCod = "") para que el select arranque con todos.
  useEffect(() => {
    formik.setFieldValue("hospitalId", "");
    (async () => {
      setLoadingHospitales(true);
      // Junto con los hospitales, el catalogo de niveles: la fila trae el COD del nivel
      // pero no su nombre, y de esta lista sale `hospitalInfo`, que es lo que alimenta la
      // card y el encabezado del organigrama impreso.
      const [{ data: res }, nombreDeNivel] = await Promise.all([
        RNHEstablecimientoGet(credentials, { zonaCod, tipo: TIPO.DIRECCION }),
        nombresDeNivelPorCod(credentials),
      ]);
      // `nivelNombre` sin el prefijo `rnhEstablecimiento*` a proposito: lo resolvimos aca,
      // no viene en la response, y conviene que se note cual es cual.
      setHospitales(
        Array.isArray(res)
          ? res.map((h) => ({
            ...h,
            nivelNombre: nombreDeNivel[h.rnhEstablecimientoNivelCod] ?? h.rnhEstablecimientoNivelCod,
          }))
          : []
      );
      setLoadingHospitales(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zonaCod]);

  // Alto del área scrolleable = desde su tope hasta el fondo del viewport (menos un
  // padding), para que la barra de scroll y su flecha de abajo NO se corten. Es
  // determinístico (mide su posición real), sin depender de la cadena de alturas del
  // layout. Se recalcula al redimensionar o al cambiar el alto del toolbar (card).
  const cascadaRef = useRef(null);
  const gridRef = useRef(null); // grilla de la cascada (ancla para la capa de conectores)
  useLayoutEffect(() => {
    const el = cascadaRef.current;
    if (!el) return;
    // Aire al fondo: el contenedor termina este margen por encima del borde del viewport.
    const AIRE_FONDO = 48;
    const ajustar = () => {
      const top = el.getBoundingClientRect().top;
      el.style.maxHeight = `${Math.max(200, window.innerHeight - top - AIRE_FONDO)}px`;
    };
    ajustar();
    window.addEventListener("resize", ajustar);
    return () => window.removeEventListener("resize", ajustar);
  }, [hospitalInfo, hospitalSeleccionado]);

  // Nota: el hospital NO se aplica al cambiar el select. El filtrado se dispara
  // solo al tocar "Filtrar" (formik.onSubmit → setHospitalSeleccionado).

  const borrarFiltros = () => {
    formik.setValues({ zonaCod: "", hospitalId: "" });
    setHospitalSeleccionado(null);
    setHospitalInfo(null);
  };

  // El select de Hospital NO tiene "Todos": arranca en un placeholder "Seleccione".
  const hospitalOptions = [
    { rnhEstablecimientoId: "", rnhEstablecimientoNombre: "Seleccione un hospital" },
    ...hospitales,
  ];

  const inputsRender = [
    { type: "select", name: "zonaCod", label: "Zona", disabled: loadingZonas, options: zonas, firstOptionKey: "rnhZonaCod", firstOptionName: "rnhZonaNombre", firstOptionValue: "rnhZonaCod", all: true },
    { type: "select", name: "hospitalId", label: "Hospital", disabled: loadingHospitales, options: hospitalOptions, firstOptionKey: "rnhEstablecimientoId", firstOptionName: "rnhEstablecimientoNombre", firstOptionValue: "rnhEstablecimientoId", all: false, width: "250px" },
  ];

  // ── Cuerpo de la cascada: mensajes / carga / error / grilla ──────────────
  const renderCascada = () => {
    if (!hospitalSeleccionado) {
      return <p className={org.cascadaMensaje}>Elegí un hospital y tocá Filtrar para ver su estructura.</p>;
    }
    if (arbol.cargando && !arbol.hayNodos) {
      return <div className={org.cascadaCargando}><CircularIndeterminate /></div>;
    }
    if (arbol.error) {
      return (
        <div className={org.cascadaError}>
          <span>No se pudo cargar la estructura.</span>
          <button type="button" className={org.cascadaReintentar} onClick={() => arbol.recargar()}>
            Reintentar
          </button>
        </div>
      );
    }
    // Siempre se muestra la grilla con la card raíz "Dirección y Staff" (aunque el
    // hospital no tenga estructura: la Dirección aparece igual, sin hijos para expandir).
    return (
      <div className={org.cascadaGrid} ref={gridRef}>
        <ConectoresCascada gridRef={gridRef} placements={arbol.placements} padrePorId={arbol.padrePorId} />
        {arbol.placements.map(({ nodo, col, row }) => (
          <NodoCascada
            key={`${nodo.rnhTipoEstructuraCod}-${nodo.rnhEstablecimientoEstructuraId}`}
            nodo={nodo}
            col={col}
            row={row}
            activo={arbol.seleccionId === nodo.rnhEstablecimientoEstructuraId}
            expandido={arbol.estaExpandido(nodo.rnhEstablecimientoEstructuraId)}
            expandible={arbol.tieneHijos(nodo)}
            agentes={arbol.agentesPropios.get(nodo.rnhEstablecimientoEstructuraId) ?? 0}
            agentesEnRama={arbol.agentesEnRama.get(nodo.rnhEstablecimientoEstructuraId) ?? 0}
            onSelect={arbol.seleccionar}
            onToggle={arbol.toggleExpandir}
          />
        ))}
      </div>
    );
  };

  // Zona y area comparten renglon en la card. Se arma aca y no en el JSX para que el guion
  // aparezca solo cuando estan las dos: si falta una, se muestra la otra sin el separador
  // colgando, y si no viene ninguna la linea entera no se dibuja.
  const zonaYArea = [
    hospitalInfo?.rnhEstablecimientoZonaNombre,
    hospitalInfo?.rnhEstablecimientoAreaNombre,
  ]
    .filter(Boolean)
    .join(" - ");

  return (
    <main className={styles.mainScroll} style={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
      <div className={styles["filters-actions"]} style={{ marginTop: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Filters formik={formik} inputsRender={inputsRender} disabled={!hospitalId} />
          <HeaderButton
            text={"Borrar filtros"}
            onClick={borrarFiltros}
            disabled={!zonaCod && !hospitalId}
          >
            <FilterAltOffRoundedIcon />
          </HeaderButton>

          {/* Card de info del hospital APLICADO. Pegada al lado de los filtros;
              solo aparece con un hospital filtrado.

              Tres renglones: nombre, nivel, y zona con area juntas. Los datos que no
              vienen no dejan hueco, directamente no se dibuja el renglon. */}
          {hospitalInfo && (
            <div className={org.hospitalCard}>
              <span className={org.hospitalCardNombre}>
                {hospitalInfo.rnhEstablecimientoNombre}
              </span>
              {hospitalInfo.nivelNombre && (
                <span className={org.hospitalCardDato}>
                  {hospitalInfo.nivelNombre}
                </span>
              )}
              {zonaYArea && (
                <span className={org.hospitalCardDato}>{zonaYArea}</span>
              )}
            </div>
          )}
        </div>

        <Buttons>
          <HeaderButton
            text={arbol.hayExpandidos ? "Colapsar todo" : "Expandir todo"}
            onClick={() => (arbol.hayExpandidos ? arbol.colapsarTodo() : arbol.expandirTodo())}
            disabled={!hospitalSeleccionado || !arbol.hayNodos}
          >
            {arbol.hayExpandidos ? <ZoomInMapRounded /> : <ZoomOutMapRounded />}
          </HeaderButton>
          <HeaderButton
            text={"Actualizar"}
            onClick={() => arbol.recargar()}
            disabled={!hospitalSeleccionado}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Imprimir / PDF"}
            onClick={() => imprimirArbol(arbol.roots, arbol.hijosPorId, hospitalInfo)}
            disabled={!hospitalSeleccionado || !arbol.hayNodos}
          >
            <PrintOutlined />
          </HeaderButton>
          {/* La Dirección queda afuera: es una raíz sintética del front, sin id real
              en la base, así que no puede tener agentes propios. */}
          {/* <HeaderButton
            text={"Ver agentes"}
            onClick={() => setDetalleOpen(true)}
            disabled={!nodoAccion || nodoAccion.rnhTipoEstructuraCod === TIPO_DIR}
          >
            <GroupsOutlined />
          </HeaderButton> */}
          <HeaderButton
            text={"Gestionar"}
            onClick={() => setAccionOpen(true)}
            disabled={!puedeGestionar || !nodoGestionable}
          >
            <EditOutlinedIcon />
          </HeaderButton>
        </Buttons>
      </div>

      {/* Vista en cascada: encabezados fijos + árbol escalonado. El alto lo fija JS
          (useLayoutEffect) según la posición en el viewport, para que la barra no se corte. */}
      <div className={org.cascadaWrap} ref={cascadaRef}>
        <div className={org.cascadaHeaders}>
          {COLUMNAS.map((titulo) => (
            <div key={titulo} className={org.cascadaHeaderCell}>{titulo}</div>
          ))}
        </div>
        {renderCascada()}
      </div>

      {/* Asignar al nodo seleccionado: Dirección (deptos → hospital), Departamento (todo),
          División (unidades), Unidad (CAPs). */}
      <Modal
        // Tambien aca y no solo en el boton: asi la regla vale cualquiera sea el camino por
        // el que `accionOpen` haya quedado en true.
        open={puedeGestionar && accionOpen && !!targetAccion}
        title={`Gestionar ${[NOMBRE_TIPO[nodoGestionable?.rnhTipoEstructuraCod], nodoGestionable?.rnhEstructuraNombre].filter(Boolean).join(" ")}`}
        onClose={cerrarAccion}
      >
        {targetAccion && (
          <ModalAsignacion target={targetAccion} onCambio={marcarCambio} onCerrar={cerrarAccion} />
        )}
      </Modal>

      {/* Detalle del nodo: de solo lectura, los agentes que trabajan ahi. */}
      <Modal
        size="lg"
        open={detalleOpen && !!nodoAccion}
        title={`Agentes de ${nodoAccion?.rnhEstructuraNombre ?? ""}`}
        onClose={() => setDetalleOpen(false)}
      >
        {nodoAccion && (
          <ModalDetalleNodo
            nodo={nodoAccion}
            hospitalId={hospitalSeleccionado}
            onCerrar={() => setDetalleOpen(false)}
          />
        )}
      </Modal>
    </main>
  );
}
